import { FC, useCallback, useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { AppDispatch } from 'store';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearSearchAktivitetResult, searchAktivitet, setSearchCriteria } from 'store/searchAktivitetSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaSpinner } from 'raa-react';

import { DrawerBody, DrawerContainer, NavigationDrawerHead } from 'components/drawerSections/DrawerSections';
import { activeSource, replaceInventeringarInActiveSource } from 'components/map/Layers';
import Pagination from 'components/search/Pagination';
import { getStringArray } from 'components/search/common';
import InventeringSearchCriterias from 'components/search/inventering/InventeringSearchCriterias';

import { SearchAktivitetCriteria } from 'models/apiModels/SearchAktivitetCriteria';
import { SearchAktivitetPost } from 'models/apiModels/SearchAktivitetPost';
import { SearchResultAktiviteter } from 'models/apiModels/SearchResultAktiviteter';

import { AktivitetStatusEnum, MapModeEnum } from 'enums';

const InventeringSearchResult: FC = () => {
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(
    searchParams.get('page') ? Number.parseInt(searchParams.get('page')!) : 0
  );
  const [criteria, setCritera] = useState<SearchAktivitetCriteria>();

  const { requestStatus, data: aktivitetResult } = useAppSelector((state: RootState) => state.searchAktiviteter);

  const pageSize = 50;

  const getStatusTyper = (paramValue: string | null): AktivitetStatusEnum[] | undefined => {
    return paramValue?.split(',').map((s) => s as AktivitetStatusEnum);
  };

  const clearResult = (dispatch: AppDispatch) => {
    activeSource.clear();
    activeSource.changed();
    dispatch(clearSearchAktivitetResult());
  };

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.SEARCH));

    // Cleanup
    return () => {
      dispatch(setMapMode(MapModeEnum.DEFAULT));
      setDrawerMobileTemplate(undefined);
    };
  }, [dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (
      searchParams.has('aktivitetstatustyper') ||
      searchParams.has('ansvarig_organisation') ||
      searchParams.has('kommun_kod') ||
      searchParams.has('lan_kod')
    ) {
      setCritera({
        aktivitetstatustyper: getStatusTyper(searchParams.get('aktivitetstatustyper')),
        ansvarig_organisation: getStringArray(searchParams.get('ansvarig_organisation')),
        kommun_kod: getStringArray(searchParams.get('kommun_kod')),
        lan_kod: getStringArray(searchParams.get('lan_kod'))
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (criteria) {
      const post: SearchAktivitetPost = {
        criteria: criteria,
        page: currentPage,
        pagesize: pageSize
      };
      dispatch(setSearchCriteria(post.criteria));
      dispatch(searchAktivitet(post));
    }

    return () => clearResult(dispatch);
  }, [criteria, dispatch, currentPage]);

  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set('page', currentPage.toString());
        return params;
      },
      { replace: true }
    );
  }, [currentPage, setSearchParams]);

  useInitialFocusOnExtent(
    useCallback(() => {
      if (aktivitetResult?.results && aktivitetResult?.results.length > 0) {
        replaceInventeringarInActiveSource(aktivitetResult?.results);
      } else {
        clearResult(dispatch);
      }
    }, [aktivitetResult?.results, dispatch])
  );

  useEffect(() => {
    if (aktivitetResult && aktivitetResult.total_size > 0) {
      setDrawerMobileTemplate(
        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <h1 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">Sökresultat</h1>
          <div className="tw-flex tw-items-center tw-justify-start">
            <p className="tw-mb-2">{aktivitetResult?.total_size} inventeringar</p>
          </div>
        </div>
      );
    }
  }, [aktivitetResult, setDrawerMobileTemplate]);

  return (
    <DrawerContainer>
      <NavigationDrawerHead />
      {requestStatus === 'pending' ? (
        <>
          <div className="tw-p-4 md:tw-p-6"></div>
          <DrawerBody>
            <div className="tw-p-4 md:tw-p-6">
              <div className="tw-flex tw-justify-center tw-py-8">
                <RaaSpinner size="3rem" />
              </div>
            </div>
          </DrawerBody>
        </>
      ) : (
        <>
          <div className="tw-border-b tw-border-raa-gray-3 tw-p-4 md:tw-p-6">
            <h1 className="tw-heading-md tw-mb-2">{aktivitetResult?.total_size} inventeringar</h1>
            <InventeringSearchCriterias />
          </div>
          <DrawerBody>
            <ul>
              {aktivitetResult?.results &&
                aktivitetResult.results.map((item: SearchResultAktiviteter) => {
                  return (
                    <li key={item.aktivitet_id} className="tw-cursor-pointer tw-border-b tw-border-raa-gray-2">
                      <NavLink
                        className="tw-block tw-overflow-auto tw-px-4 tw-py-2 focus:tw-outline-transparent md:tw-px-4 md:tw-py-4"
                        to={`/aktivitet/${item.aktivitet_id}`}
                      >
                        <div
                          className="tw-row-gap-2 tw-col-gap-4 tw-grid tw-min-w-full"
                          style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}
                        >
                          <div className="tw-flex tw-justify-between">
                            <div className="tw-flex tw-flex-col tw-pt-2">
                              <span
                                aria-hidden="true"
                                className="tw-flex tw-h-[20px] tw-w-[20px] tw-items-center tw-justify-center tw-rounded-full tw-bg-raa-blue tw-text-raa-white"
                              >
                                <span className="raa-icon-collection tw-text-size-0.813" />
                              </span>
                            </div>
                            <div className="tw-ml-2 tw-mr-4 tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
                              <span className="tw-text-size-1 tw-font-semibold tw-text-raa-orange">
                                {item.titel} <span className="tw-italic">({item.aktivitetsnummer})</span>
                              </span>
                              <span className="tw-truncate tw-pr-3 tw-text-size-0.813">
                                {item.ansvarig_organisation_namn}
                              </span>
                            </div>
                          </div>
                          <div className="tw-flex tw-justify-between">
                            <div className="tw-flex tw-flex-col tw-overflow-hidden tw-text-size-0.813">
                              <span className="tw-truncate">
                                {item.geografisk_indelning?.kommun?.map((k) => k.kommun_namn).join(', ')}
                              </span>
                              <span className="tw-truncate">
                                {item.geografisk_indelning?.kommun?.map((k) => `${k.lan_namn}s län`).join(', ')}
                              </span>
                            </div>
                            <div className="tw-flex tw-flex-1 tw-justify-end tw-text-size-0.813">
                              <div className="tw-flex tw-flex-col tw-text-right">
                                <span className="tw-truncate">{item.aktivitetstatus_namn}</span>
                                <span className="tw-truncate">
                                  {item.start_ar}–{item.slut_ar}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </NavLink>
                    </li>
                  );
                })}
            </ul>
          </DrawerBody>
        </>
      )}

      {aktivitetResult && aktivitetResult.total_size >= pageSize && (
        <Pagination results={aktivitetResult} setCurrentPage={setCurrentPage} requestStatus={requestStatus} />
      )}
    </DrawerContainer>
  );
};

export default InventeringSearchResult;
